import { Icon, type IconName } from '@insio/ui-toolkit';
import { AiChatDisplayMode } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** The three chat surfaces the user can pick from, in display order. */
const DISPLAY_MODES: ReadonlyArray<{ mode: AiChatDisplayMode; icon: IconName }> = [
  { mode: AiChatDisplayMode.BUBBLE, icon: 'message' },
  { mode: AiChatDisplayMode.DOCKED, icon: 'grid-2' },
  { mode: AiChatDisplayMode.HIDDEN, icon: 'eye-slash' }
];

/** Translation key suffix per mode — lowercase mirrors the JSON structure. */
const MODE_KEY: Record<AiChatDisplayMode, string> = {
  [AiChatDisplayMode.BUBBLE]: 'bubble',
  [AiChatDisplayMode.DOCKED]: 'docked',
  [AiChatDisplayMode.HIDDEN]: 'hidden'
};

interface ChatDisplayModePickerProps {
  value: AiChatDisplayMode;
  onChange: (mode: AiChatDisplayMode) => void;
  disabled?: boolean;
}

/**
 * Radio group choosing where the chat surface lives (bubble, docked, hidden).
 * Plain buttons on purpose — the toolkit Button cannot carry the radio semantics
 * and the icon-over-label column layout these tiles need.
 */
export function ChatDisplayModePicker({ value, onChange, disabled }: ChatDisplayModePickerProps) {
  const t = useFeatureTranslation('aiChat');

  return (
    <div role='radiogroup' className='mt-2xs flex gap-xs'>
      {DISPLAY_MODES.map(({ mode, icon }) => {
        const active = value === mode;
        return (
          <button
            key={mode}
            type='button'
            role='radio'
            aria-checked={active}
            disabled={disabled}
            data-testid={`chat-display-mode-${MODE_KEY[mode]}`}
            onClick={() => onChange(mode)}
            className={`flex flex-1 flex-col items-center gap-2xs rounded-lg border px-sm py-sm text-xs transition-colors ${
              active
                ? 'border-secondary-base bg-secondary-surface text-secondary-base'
                : 'border-border-base text-foreground-muted hover:bg-background-muted'
            }`}
          >
            <Icon name={icon} className='h-4 w-4' />
            {t(`settings.displayMode.${MODE_KEY[mode]}`)}
          </button>
        );
      })}
    </div>
  );
}
